import type {
  Connection,
  Matter,
  MedicalTreatment,
  MedicalTreatmentAppointment,
  MedicalTreatmentBill,
  MedicalTreatmentPlan,
  MedicalTreatmentRecord,
} from "@/lib/types";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import dayjs from "dayjs";
import { getTreatmentAppointmentStartsAt } from "@/lib/utils/matters/medical-treatment-appointment-dates";

export type TreatmentViewModel = MedicalTreatment & {
  appointments: (MedicalTreatmentAppointment & {
    records: (MedicalTreatmentRecord & {
      hasReference: boolean;
    })[];
    plans: MedicalTreatmentPlan[];
    allRecordsReceived: boolean;
    receivedRecordsCount: number;
    recordsNotApplicable: boolean;
    bills: (MedicalTreatmentBill & {
      totalCost: number;
      hasReference: boolean;
    })[];
    allBillsReceived: boolean;
    receivedBillsCount: number;
    hasMedicalConnections: boolean;
    hasBillingConnections: boolean;
    billsNotApplicable: boolean;
  })[];
  totalCostSum: number;
  records: (MedicalTreatmentRecord & {
    hasReference: boolean;
  })[];
  bills: (MedicalTreatmentBill & {
    totalCost: number;
    hasReference: boolean;
  })[];
  plans: MedicalTreatmentPlan[];
  provider?: Connection;
  treatingPhysician?: Connection;
  nextAppointment: MedicalTreatmentAppointment | null;
  everyAppointmentHasMedicalConnections: boolean;
  missingAppointmentMedicalConnectionsCount: number;
  everyAppointmentHasBillingConnections: boolean;
  missingAppointmentBillingConnectionsCount: number;
  allRecordsHaveReferences: boolean;
  allBillsHaveReferences: boolean;
  recordsWithReferencesCount: number;
  billsWithReferencesCount: number;
};

type TreatmentWithAppointments = Pick<TreatmentViewModel, "appointments"> & {
  provider?: Pick<Connection, "contact">;
};

const getEarliestAppointmentDateTime = (
  treatment: TreatmentWithAppointments,
) => {
  if (treatment.appointments.length === 0) {
    return null;
  }

  return treatment.appointments.reduce<number | null>(
    (earliest, appointment) => {
      const date = appointment.date;

      if (!date) {
        return earliest;
      }

      const appointmentDate = dayjs(date);
      if (!appointmentDate.isValid()) {
        return earliest;
      }

      const timeParts = appointment.time?.split(":").map(Number);
      const hasValidTime =
        timeParts &&
        timeParts.length >= 2 &&
        timeParts.every((part) => Number.isFinite(part));
      const appointmentDateTime = hasValidTime
        ? appointmentDate
            .startOf("day")
            .hour(timeParts[0])
            .minute(timeParts[1])
            .second(timeParts[2] ?? 0)
        : appointmentDate.startOf("day");
      const appointmentTimestamp = appointmentDateTime.valueOf();

      return earliest === null || appointmentTimestamp < earliest
        ? appointmentTimestamp
        : earliest;
    },
    null,
  );
};

export const sortTreatmentsByEarliestAppointment = <
  T extends TreatmentWithAppointments,
>(
  treatments: T[],
) => {
  return [...treatments].sort((a, b) => {
    const earliestA = getEarliestAppointmentDateTime(a);
    const earliestB = getEarliestAppointmentDateTime(b);

    if (earliestA !== null && earliestB !== null) {
      if (earliestA !== earliestB) {
        return earliestA - earliestB;
      }
    }

    if (earliestA !== null && earliestB === null) return -1;
    if (earliestA === null && earliestB !== null) return 1;
    const nameA = getConnectionContactName(a.provider?.contact)?.toLowerCase();
    const nameB = getConnectionContactName(b.provider?.contact)?.toLowerCase();
    if (!nameA && !nameB) return 0;
    if (!nameA) return 1;
    if (!nameB) return -1;

    return nameA.localeCompare(nameB);
  });
};

/**
 * @param matter
 * @returns A structured list of medical treatments with their providers, records, bills, and appointments.
 * The appointments also include the connected records and bills.
 */
export const useMedicalTreatmentViewModel = (matter?: Matter) => {
  if (!matter) {
    return [];
  }
  const fileReferences = matter.fileReferences;
  const medicalTreatmentAppointmentConnections =
    matter.medicalTreatmentAppointmentConnections;
  const medicalTreatmentAppointments = matter.medicalTreatmentAppointments;
  const medicalTreatmentBills = matter.medicalTreatmentBills;
  const medicalTreatmentPlans = matter.medicalTreatmentPlans;
  const medicalTreatmentRecords = matter.medicalTreatmentRecords;
  const medicalTreatments = matter.medicalTreatments;

  const medicalTreatmentsWithProviders = medicalTreatments.map((treatment) => {
    const connections = treatment.connections;
    const records = medicalTreatmentRecords
      .filter((record) => record.medicalTreatmentId === treatment.id)
      .map((record) => {
        const hasReference = fileReferences.some(
          (reference) => reference.medicalTreatmentRecordId === record.id,
        );
        return {
          ...record,
          hasReference,
        };
      });
    const bills = medicalTreatmentBills
      .filter((bill) => bill.medicalTreatmentId === treatment.id)
      .map((bill) => {
        const hasReference = fileReferences.some(
          (reference) => reference.medicalTreatmentBillId === bill.id,
        );
        return {
          ...bill,
          totalCost: calculateMedicalTreatmentBillTotals([bill]).totalBill,
          hasReference,
        };
      });
    const appointmentConnections = medicalTreatmentAppointmentConnections;
    const treatmentAppointments = medicalTreatmentAppointments.filter(
      (appointment) => appointment.medicalTreatmentId === treatment.id,
    );
    const treatmentAppointmentIds = new Set(
      treatmentAppointments.map((appointment) => appointment.id),
    );
    const treatmentPlanIds = new Set(
      appointmentConnections.flatMap((connection) =>
        connection.medicalTreatmentPlanId &&
        treatmentAppointmentIds.has(connection.medicalTreatmentAppointmentId)
          ? [connection.medicalTreatmentPlanId]
          : [],
      ),
    );
    const plans = medicalTreatmentPlans.filter((plan) =>
      treatmentPlanIds.has(plan.id),
    );
    const plansById = new Map(plans.map((plan) => [plan.id, plan]));
    const appointments = treatmentAppointments.map((appointment) => {
      const seenRecordIds = new Set<string>();
      const appointmentRecords = appointmentConnections
        .filter(
          (connection) =>
            connection.medicalTreatmentAppointmentId === appointment.id,
        )
        .map((connection) => {
          const record = records.find(
            (record) => record.id === connection.medicalTreatmentRecordId,
          );
          if (!record) {
            return;
          }
          if (seenRecordIds.has(record.id)) {
            return;
          }
          seenRecordIds.add(record.id);
          const hasReference = fileReferences.some(
            (reference) => reference.medicalTreatmentRecordId === record.id,
          );
          return {
            ...record,
            hasReference,
          };
        })
        .filter((record) => record !== undefined);
      const seenBillIds = new Set<string>();
      const appointmentBills = appointmentConnections
        .filter(
          (connection) =>
            connection.medicalTreatmentAppointmentId === appointment.id,
        )
        .map((connection) => {
          const bill = bills.find(
            (bill) => bill.id === connection.medicalTreatmentBillId,
          );
          if (!bill) {
            return;
          }
          if (seenBillIds.has(bill.id)) {
            return;
          }
          seenBillIds.add(bill.id);
          const hasReference = fileReferences.some(
            (reference) => reference.medicalTreatmentBillId === bill.id,
          );
          return {
            ...bill,
            hasReference,
          };
        })
        .filter((bill) => bill !== undefined);
      const seenPlanIds = new Set<string>();
      const appointmentPlans = appointmentConnections
        .filter(
          (connection) =>
            connection.medicalTreatmentAppointmentId === appointment.id,
        )
        .map((connection) => {
          const planId = connection.medicalTreatmentPlanId;
          if (!planId || seenPlanIds.has(planId)) {
            return;
          }
          const plan = plansById.get(planId);
          if (!plan) {
            return;
          }
          seenPlanIds.add(planId);
          return plan;
        })
        .filter((plan) => plan !== undefined);
      const recordsNotApplicable = Boolean(treatment.recordNotApplicable);
      const billsNotApplicable = Boolean(treatment.billNotApplicable);
      return {
        ...appointment,
        records: appointmentRecords,
        plans: appointmentPlans,
        recordsNotApplicable,
        hasMedicalConnections:
          recordsNotApplicable || appointmentRecords.length > 0,
        allRecordsReceived:
          recordsNotApplicable ||
          (appointmentRecords.length > 0 &&
            appointmentRecords.every((record) => record.status === "Received")),
        receivedRecordsCount: appointmentRecords.filter(
          (record) => record.status === "Received",
        ).length,
        bills: appointmentBills,
        billsNotApplicable,
        hasBillingConnections:
          billsNotApplicable || appointmentBills.length > 0,
        allBillsReceived:
          billsNotApplicable ||
          (appointmentBills.length > 0 &&
            appointmentBills.every((bill) => bill.recordStatus === "Received")),
        receivedBillsCount: appointmentBills.filter(
          (bill) => bill.recordStatus === "Received",
        ).length,
      };
    });

    const completeTreatment = {
      ...treatment,
      appointments,
      records,
      bills,
      plans,
      totalCostSum: bills.reduce((acc, bill) => acc + bill.totalCost, 0),
      provider: connections.find(
        (connection) =>
          connection.connectionType === "matter_medical_treatment_to_provider",
      ),
      treatingPhysician: connections.find(
        (connection) =>
          connection.connectionType ===
          "matter_medical_treatment_to_treating_physician",
      ),
      totalBills: bills.reduce(
        (acc, bill) => acc + (bill.originalBill ?? 0),
        0,
      ),
      nextAppointment: [...appointments]
        .sort(
          (a, b) =>
            getTreatmentAppointmentStartsAt(a).valueOf() -
            getTreatmentAppointmentStartsAt(b).valueOf(),
        )
        .find(
          (appointment) =>
            (appointment.outcome == null ||
              appointment.outcome === "scheduled") &&
            appointment.date &&
            getTreatmentAppointmentStartsAt(appointment).isAfter(dayjs()),
        ),
      receivedBillsCount: 0,
      receivedRecordsCount: 0,
      allRecordsReceived: true,
      allBillsReceived: true,
      everyAppointmentHasMedicalConnections: appointments.every(
        (appointment) => appointment.hasMedicalConnections,
      ),
      missingAppointmentMedicalConnectionsCount: appointments.filter(
        (appointment) => !appointment.hasMedicalConnections,
      ).length,
      everyAppointmentHasBillingConnections: appointments.every(
        (appointment) => appointment.hasBillingConnections,
      ),
      missingAppointmentBillingConnectionsCount: appointments.filter(
        (appointment) => !appointment.hasBillingConnections,
      ).length,
      allRecordsHaveReferences:
        records.length > 0 && records.every((record) => record.hasReference),
      allBillsHaveReferences:
        bills.length > 0 && bills.every((bill) => bill.hasReference),
      recordsWithReferencesCount: records.filter(
        (record) => record.hasReference,
      ).length,
      billsWithReferencesCount: bills.filter((bill) => bill.hasReference)
        .length,
    };
    completeTreatment.bills.forEach((bill) => {
      if (bill.recordStatus !== "Received") {
        completeTreatment.allBillsReceived = false;
      }
      if (bill.recordStatus === "Received") {
        completeTreatment.receivedBillsCount++;
      }
    });
    completeTreatment.records.forEach((record) => {
      if (record.status !== "Received") {
        completeTreatment.allRecordsReceived = false;
      }
      if (record.status === "Received") {
        completeTreatment.receivedRecordsCount++;
      }
    });
    return completeTreatment;
  });
  return medicalTreatmentsWithProviders as TreatmentViewModel[];
};
